import { ref } from 'vue';
import { CatalogHostedResources, CatalogFirebaseResources, AppHostedResources } from 'ihunter-common';
import { useFirebaseDatabase } from './useFirebaseDatabase';
import { useEnvironment } from './useEnvironment';
import { useApi } from './useApi';
import { LayerGroup } from './useConstants';

const catalog = ref(undefined);
const products = ref(undefined);
const prices = ref(undefined);

let loadingPromise = null;

const USE_FIREBASE_CATALOG = false; // DEV: Set to true to enable Firebase catalog and load all metadata from Firebase instead of the resource server

// Provides the complete product catalog and resources
export function useCatalog() {
    const { config } = useEnvironment();
    const { httpGetJson } = useApi('cache');
    const { getData } = useFirebaseDatabase();

    async function load() {
        if (loadingPromise) {
            return loadingPromise;
        }

        const catalogRequest = getCatalog()
            .then((value) => {
                catalog.value = value;
            })
            .catch((error) => {
                console.error('Failed to load catalog', error);
            });

        const pricesRequest = getProductPrices()
            .then((value) => {
                prices.value = value;
            })
            .catch((error) => {
                console.error('Failed to load product prices', error);
            });

        const productsRequest = getProductSkus()
            .then((value) => {
                products.value = value;
            })
            .catch((error) => {
                console.error('Failed to load product SKUs', error);
            });

        loadingPromise = Promise.allSettled([catalogRequest, pricesRequest, productsRequest])
            .then(() => {
                console.log('Product catalog, prices and SKUs loaded from server');
            })
            .finally(() => {
                loadingPromise = null;
            });

        return loadingPromise;
    }

    if ((!catalog.value || !prices.value || !products.value) && !loadingPromise) {
        void load(); // Fire and forget, don't await
    }

    async function getCatalog() {
        if (USE_FIREBASE_CATALOG) {
            return getData(CatalogFirebaseResources.Catalog());
        }
        return httpGetJson(AppHostedResources.Catalog());
    }

    function getRegionMetadata(region) {
        if (USE_FIREBASE_CATALOG) {
            return getData(CatalogFirebaseResources.RegionMetadata(region));
        }
        return httpGetJson(CatalogHostedResources.RegionMetadata(region));
    }

    function getProductMetadata(region, product) {
        if (USE_FIREBASE_CATALOG) {
            return getData(CatalogFirebaseResources.ProductMetadata(region, product));
        }
        return httpGetJson(CatalogHostedResources.ProductMetadata(region, product));
    }

    async function getResourceMetadata(region, layerGroup, layerId) {
        if (USE_FIREBASE_CATALOG) {
            return getData(CatalogFirebaseResources.ResourceLayerMetadata(region, layerGroup, layerId));
        }
        return httpGetJson(CatalogHostedResources.ResourceLayerMetadata(region, layerGroup, layerId));
    }

    async function getResourceLayerMetadata(region, layerGroup, layerId, product) {
        if (layerGroup === LayerGroup.County) {
            return httpGetJson(CatalogHostedResources.CountyProductMetadata(region, product));
        } else if (layerGroup === LayerGroup.Subscription) {
            return httpGetJson(CatalogHostedResources.SubscriptionLayerMetadata(region, layerGroup, layerId));
        }
        return httpGetJson(CatalogHostedResources.ResourceLayerMetadata(region, layerGroup, layerId));
    }

    async function getResourceLayerBoundaryGeoJson(region, layerGroup, layerId) {
        return httpGetJson(CatalogHostedResources.ResourceLayerBoundaryGeoJson(region, layerGroup, layerId));
    }

    async function getResourceLayerProductMetadata(region, layerGroup, layerId, map, product) {
        return getData(CatalogFirebaseResources.ProductMetadata(region, layerGroup, layerId, map, product));
    }

    async function getCountyLayerBoundaryGeoJson(region, county) {
        return httpGetJson(CatalogHostedResources.CountyBoundaryGeoJson(region, county));
    }

    function getCountyLayerBoundaryTiles(region, product) {
        return CatalogHostedResources.CountyBoundaryTiles(region, product);
    }

    function getResourceLayerRasterTileUrl(region, layerId) {
        return config.value?.url?.tiles + CatalogHostedResources.ResourceLayerRasterTiles(region, layerId);
    }

    async function getProductPrices() {
        return getData(CatalogFirebaseResources.AllProductPrices());
    }

    async function getProductSkus() {
        return getData(CatalogFirebaseResources.AllProductSkus());
    }

    return {
        catalog,
        products,
        prices,

        getRegionMetadata,
        getProductMetadata,
        getResourceMetadata,
        getResourceLayerMetadata,

        getResourceLayerBoundaryGeoJson,
        getResourceLayerProductMetadata,
        getCountyLayerBoundaryGeoJson,
        getCountyLayerBoundaryTiles,
        getResourceLayerRasterTileUrl,
    };
}
